// The PAID FUNNEL's own eligibility rule (QualForm.jsx's wizard), extracted
// from that component at act:6d293884 so it can be EXECUTED and locked.
//
// WHY THIS IS A SEPARATE MODULE AND NOT A CALL INTO qualificationGate.js.
// The divergence is deliberate and operator-held: act:22274f4d §6 shipped the
// case-file gate NEW-ENDPOINT-ONLY, and act:06c0f539 governs if and when the
// funnel's copy converges. This extraction does NOT collapse the two — the
// funnel keeps its own rule, in its own file, and nothing here imports the
// case-file gate. What changes is only that the rule is now importable, which
// is what lets the parity harness run it over declared verdicts. Before this,
// the funnel's comparison lived inline in JSX, so it was the ONE comparison
// site with no executable lock — on the surface that carries paid ad traffic.
// If act:06c0f539 is ever un-gated, converge THIS module onto
// qualificationGate.js rather than growing a fourth copy.
//
// THE VOCABULARY — `expected` has three legal states (act:6d293884). The
// authoritative statement lives in app/models/qualification_gate.rb's header;
// mirrored here, not restated:
//
//   true / false      GATING — answering anything else turns the visitor away.
//   present + null    INFORMATIONAL — either answer advances; we record it and
//                     nobody is turned away. This is what lets the firm learn
//                     something (e.g. which claimants are in Virginia) without
//                     losing the ones who answer "no".
//   anything else     MALFORMED (key ABSENT, or non-boolean non-null) —
//                     fail-closed: the visitor is turned away, exactly as
//                     before. Loud-failing this class is now also handled
//                     upstream by LegalTheory#qualifying_questions_structure,
//                     which refuses to SAVE it.

const isBool = (v) => v === true || v === false

// Mirrors QualificationGate.informational? / qualificationGate.js
// isInformational. The own-property test is LOAD-BEARING: an ABSENT `expected`
// and an explicit null mean OPPOSITE things (malformed vs informational).
// Never relax `=== null` to `== null` and never drop the own-property check —
// either mutation makes a mis-authored question silently non-gating on the
// live paid funnel.
export function isInformational(question) {
  return (
    !!question &&
    typeof question === "object" &&
    !Array.isArray(question) &&
    Object.prototype.hasOwnProperty.call(question, "expected") &&
    question.expected === null
  )
}

// Does this answer move the wizard on, or turn the visitor away?
//
// ONE predicate serves both the live answer handler and the sessionStorage
// restore, because they have always asked the same question — the restore just
// asked it as `a !== q.expected` and broke the prefix. Keeping them one
// function is what stops them drifting apart.
//
// The `isBool(given)` guard is a deliberate hardening over the inline original
// (`given === q.expected`), and it cannot change live behavior: the wizard's
// only callers pass a real boolean from the Yes/No buttons. It closes a latent
// fail-open the inline form carried — with an ABSENT `expected` and an
// undefined `given`, `undefined === undefined` advanced.
export function funnelAdvances(question, given) {
  if (!isBool(given)) return false
  if (isInformational(question)) return true
  return given === (question && typeof question === "object" ? question.expected : undefined)
}

// The fail-closed sessionStorage restore: restored answers feed
// qualification_answers — the attested legal record — so nothing is trusted
// from storage. Only answers that CLEAR their question are kept, and only as a
// contiguous prefix in question order, so a newly-inserted question can never
// be skipped. The stored step is not consulted at all; it is recomputed here as
// the length of that prefix.
//
// For an INFORMATIONAL question this now keeps EITHER answer. Under the old
// rule a visitor who answered "no" had their restore truncated at that
// question and silently lost the rest of their progress — the answer was valid,
// it just did not equal `expected`.
export function restoredAnswerPrefix(questions, stored) {
  const answers = {}
  let step = 0
  if (!Array.isArray(questions) || !stored || typeof stored !== "object") return { step, answers }

  for (const q of questions) {
    const id = q && typeof q === "object" ? q.id : null
    if (typeof id !== "string") break
    const a = stored[id]
    if (!funnelAdvances(q, a)) break
    answers[id] = a
    step += 1
  }
  return { step, answers }
}
